import { SearchOutlined } from "@ant-design/icons"
import { Button, Flex, Tag, Typography } from "antd"
import React, { useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { DateRangeFilter } from "../../../../components/Filters/DateRangeFilter.tsx"
import { SEARCH_DEBOUNCE_MS } from "../../../../components/Input/search-debounce.ts"
import { TextInput } from "../../../../components/Input/TextInput.tsx"
import ResolvedSelect from "../../../../components/Select/ResolvedSelect.tsx"
import { useDebouncedCallback } from "../../../../hooks/use-debounced-callback.ts"
import { activeFilterCount } from "../../helpers/tracking-query.ts"
import {
  ENVELOPE_STATUSES,
  PARTY_TYPES,
  SUBJECT_TYPES
} from "../../types/esign.types.ts"
import type {
  TrackingFilterOptions,
  TrackingFilters
} from "../../types/tracking.types.ts"

const { Text } = Typography

function Field({
  label,
  className,
  children
}: {
  label: string
  className?: string
  children: React.ReactNode
}) {
  return (
    <Flex vertical gap={4} className={`min-w-0 ${className ?? ""}`}>
      <Text type={"secondary"} className={"text-xs font-medium"}>
        {label}
      </Text>
      {children}
    </Flex>
  )
}

interface Props {
  filters: TrackingFilters
  setFilters: (patch: Partial<TrackingFilters>) => void
  clearFilters: () => void
  senders: TrackingFilterOptions["senders"]
}

export function TrackingFiltersBar({
  filters,
  setFilters,
  clearFilters,
  senders
}: Props) {
  const { t } = useTranslation()
  const [term, setTerm] = useState(filters.q)
  // What the box shows now (exactly as typed), the trimmed q this box last wrote to the URL, and the
  // filters the latest keystroke was made against.
  const shown = useRef(filters.q)
  const written = useRef<string | null>(null)
  const typedAgainst = useRef(filters)
  const showTerm = (value: string) => {
    shown.current = value
    setTerm(value)
  }
  // The URL holds the trimmed term; an unchanged term is never written, so a drilled tile is not dropped.
  const writeTerm = (value: string) => {
    const q = value.trim()
    if (q === filters.q.trim()) return
    written.current = q
    setFilters({ q })
  }
  useEffect(() => {
    // Our own write landing in the URL must not move the box: the user may have typed on.
    const own = written.current !== null && written.current === filters.q.trim()
    written.current = null
    if (own || shown.current.trim() === filters.q.trim()) return
    showTerm(filters.q)
  }, [filters])
  // A call for a term the box no longer shows (cleared, replaced), or typed against filters that have
  // since changed from outside the box (clear, tile, back), is dropped.
  const searchAfterTyping = useDebouncedCallback((value: string) => {
    if (value === shown.current && typedAgainst.current === filters)
      writeTerm(value)
  }, SEARCH_DEBOUNCE_MS)

  const statusOptions = ENVELOPE_STATUSES.filter(
    (status) => status !== "draft"
  ).map((status) => ({
    value: status,
    label: t(`esign.statuses.${status}`)
  }))
  const typeOptions = SUBJECT_TYPES.map((type) => ({
    value: type,
    label: t(`esign.tracking.documentTypes.${type}`)
  }))
  const partyOptions = PARTY_TYPES.map((party) => ({
    value: party,
    label: t(`esign.partyTypes.${party}`)
  }))
  const senderOptions = senders.map((sender) => ({
    value: sender.id,
    label: sender.name,
    title: sender.name
  }))

  return (
    <Flex
      vertical
      gap={8}
      className={"rounded-lg border border-gray-200 bg-white px-3 py-2.5"}
    >
      <TextInput
        disableAutoCapitalize
        allowClear
        prefix={<SearchOutlined className={"text-gray-400"} />}
        placeholder={t("esign.tracking.searchPlaceholder")}
        value={term}
        onChange={(event) => {
          showTerm(event.target.value)
          typedAgainst.current = filters
          searchAfterTyping(event.target.value)
        }}
        onPressEnter={() => writeTerm(shown.current)}
        onClear={() => {
          showTerm("")
          writeTerm("")
        }}
      />
      <Flex wrap={"wrap"} gap={"small"} align={"end"}>
        <Field
          label={t("esign.tracking.filters.status")}
          className={"w-48 max-md:grow"}
        >
          <ResolvedSelect
            aria-label={t("esign.tracking.filters.status")}
            mode={"multiple"}
            maxTagCount={"responsive"}
            allowClear
            placeholder={t("esign.tracking.filters.any")}
            value={filters.status}
            options={statusOptions}
            onChange={(status) => setFilters({ status })}
          />
        </Field>
        <Field
          label={t("esign.tracking.filters.documentType")}
          className={"w-44 max-md:grow"}
        >
          <ResolvedSelect
            aria-label={t("esign.tracking.filters.documentType")}
            mode={"multiple"}
            maxTagCount={"responsive"}
            allowClear
            placeholder={t("esign.tracking.filters.any")}
            value={filters.document_type}
            options={typeOptions}
            onChange={(document_type) => setFilters({ document_type })}
          />
        </Field>
        <Field
          label={t("esign.tracking.filters.partyType")}
          className={"w-44 max-md:grow"}
        >
          <ResolvedSelect
            aria-label={t("esign.tracking.filters.partyType")}
            mode={"multiple"}
            maxTagCount={"responsive"}
            allowClear
            placeholder={t("esign.tracking.filters.any")}
            value={filters.party_type}
            options={partyOptions}
            onChange={(party_type) => setFilters({ party_type })}
          />
        </Field>
        <Field
          label={t("esign.tracking.filters.sentBy")}
          className={"w-48 max-md:grow"}
        >
          <ResolvedSelect
            aria-label={t("esign.tracking.filters.sentBy")}
            mode={"multiple"}
            maxTagCount={"responsive"}
            allowClear
            showSearch
            optionFilterProp={"title"}
            placeholder={t("esign.tracking.filters.any")}
            value={filters.sent_by}
            options={senderOptions}
            onChange={(sent_by) => setFilters({ sent_by })}
          />
        </Field>
        <Field
          label={t("esign.tracking.filters.sent")}
          className={"w-60 max-md:grow"}
        >
          <DateRangeFilter<TrackingFilters>
            after={filters.sent_after}
            before={filters.sent_before}
            afterKey={"sent_after"}
            beforeKey={"sent_before"}
            className={"w-full"}
            onChange={setFilters}
          />
        </Field>
        {activeFilterCount(filters) > 0 && (
          <Button type={"link"} onClick={clearFilters}>
            {t("esign.tracking.filters.clear")}
          </Button>
        )}
      </Flex>
      {filters.tile && (
        <Flex align={"center"} gap={8}>
          <Tag color={"blue"} className={"m-0"}>
            {t("esign.tracking.filters.tile", {
              tile: t(`esign.tracking.tiles.${filters.tile}`)
            })}
          </Tag>
          <Button
            type={"link"}
            size={"small"}
            onClick={() => setFilters({ tile: null })}
          >
            {t("esign.tracking.filters.clearTile")}
          </Button>
        </Flex>
      )}
    </Flex>
  )
}
